import apiClient from "./apiClient";
import { normalizeProductCurrency } from "@/helpers/currency";

/** Consistente con `generateLineId` en Vuex (blister opcional aquí omitido si no llega del API). */
const normalizeVariantsObject = (obj) => {
  if (!obj || typeof obj !== "object") return "";
  const keys = Object.keys(obj).sort();
  const sorted = {};
  keys.forEach((k) => {
    sorted[k] = obj[k];
  });
  return JSON.stringify(sorted);
};

/**
 * Snapshot en localStorage (invitado): metadatos del carrito en servidor para recovery multi-pestaña.
 * Los ítems siguen siendo la cookie `cart` en Vuex; esto cumple auditoría solicitada (id + ítems mínimos).
 */
export const GUEST_CART_SNAPSHOT_KEY = "guest_cart_snapshot";

export const clearGuestCartSnapshot = () => {
  try {
    localStorage.removeItem(GUEST_CART_SNAPSHOT_KEY);
  } catch (_) {
    /* ignore */
  }
};

const saveGuestCartSnapshot = (responseData, cartItems = []) => {
  if (localStorage.getItem("accessToken")) {
    clearGuestCartSnapshot();
    return;
  }
  const items = Array.isArray(cartItems)
    ? cartItems.map((it) => ({
        product_id: it.id,
        quantity: it.quantity,
        variants: it.variants || {},
        variants_label: it.variants_label || "",
        lineId: it.lineId,
      }))
    : [];
  localStorage.setItem(
    GUEST_CART_SNAPSHOT_KEY,
    JSON.stringify({
      cart_id: responseData.cart_id,
      anonymous_id:
        responseData.anonymous_id || localStorage.getItem("cart_anonymous_id"),
      server_updated_at: responseData.updated_at || responseData.last_activity,
      client_updated_at: new Date().toISOString(),
      items,
      shopping_cart_status: responseData.shopping_cart_status,
    })
  );
};

const regenerateAnonymousIdAfterAbandonment = async () => {
  localStorage.removeItem("cart_anonymous_id");
  clearGuestCartSnapshot();
  try {
    const response = await apiClient.post("/orders/public/generate-id/");
    const id = response.data?.anonymous_id;
    if (id) {
      localStorage.setItem("cart_anonymous_id", id);
      return id;
    }
  } catch (error) {
    console.error("Error al regenerar ID anónimo:", error);
    const fallback = "local_" + Date.now().toString(36) + Math.random().toString(36).slice(2, 9);
    localStorage.setItem("cart_anonymous_id", fallback);
    return fallback;
  }
  const fallback =
    "local_" + Date.now().toString(36) + Math.random().toString(36).slice(2, 9);
  localStorage.setItem("cart_anonymous_id", fallback);
  return fallback;
};

/** ID anónimo persistido (o nuevo desde servidor / fallback local) */
export const getAnonymousId = async () => {
  let anonymousId = localStorage.getItem("cart_anonymous_id");

  if (!anonymousId) {
    try {
      const response = await apiClient.post("/orders/public/generate-id/");
      anonymousId = response.data.anonymous_id;
      localStorage.setItem("cart_anonymous_id", anonymousId);
    } catch (error) {
      console.error("Error al generar ID anónimo:", error);
      anonymousId = "local_" + Date.now().toString() + Math.random().toString(36).substr(2, 9);
      localStorage.setItem("cart_anonymous_id", anonymousId);
    }
  }

  return anonymousId;
};

/**
 * Envía carrito completo al servidor (reemplaza líneas). Funciona también con usuario logeado.
 * @param {Array} cartItems - líneas locales
 * @param {{ clearCart?: boolean }} options - si true, vacía el carrito en servidor (items puede ir vacío)
 */
export const syncCartWithServer = async (cartItems, options = {}) => {
  try {
    const hasToken = !!localStorage.getItem("accessToken");
    const storedAnon = localStorage.getItem("cart_anonymous_id");
    const serverItems = Array.isArray(cartItems)
      ? cartItems
          .map((item) => ({
            product_id: item.id ?? item.product_id,
            quantity: item.quantity,
            variants: item.variants || {},
            variants_label: item.variants_label || "",
          }))
          .filter((row) => row.product_id != null && row.product_id !== "")
      : [];

    let anonymousId = null;
    if (!hasToken) {
      anonymousId = storedAnon || (await getAnonymousId());
    } else if (storedAnon) {
      // Token presente pero inválido en servidor: el backend trata como anónimo y necesita anonymous_id
      anonymousId = storedAnon;
    }

    const payload = { items: serverItems };
    if (options.clearCart) {
      payload.clear_cart = true;
    }
    if (anonymousId) {
      payload.anonymous_id = anonymousId;
    }

    const response = await apiClient.post("/orders/public/temp-cart/", payload);

    const data = response.data;
    if (data.anonymous_id) {
      localStorage.setItem("cart_anonymous_id", data.anonymous_id);
    }

    if (!hasToken && (serverItems.length > 0 || data.cart_id)) {
      saveGuestCartSnapshot(data, cartItems || []);
    } else if (hasToken || serverItems.length === 0) {
      clearGuestCartSnapshot();
    }

    return data;
  } catch (error) {
    const status = error.response?.status;
    if (!localStorage.getItem("accessToken") && (status === 410 || status === 404 || status === 401)) {
      await regenerateAnonymousIdAfterAbandonment();
    }
    console.error("Error al sincronizar carrito con el servidor:", error);
    return null;
  }
};

export const fetchCartFromServer = async () => {
  try {
    const isAuthenticated = !!localStorage.getItem("accessToken");

    if (isAuthenticated) {
      const response = await apiClient.get("/orders/cart/current/");
      return response.data;
    }

    const anonymousId = localStorage.getItem("cart_anonymous_id");
    if (!anonymousId) return null;

    const response = await apiClient.get(`/orders/public/temp-cart/${anonymousId}/`);
    return response.data;
  } catch (error) {
    const status = error.response?.status;
    if (!localStorage.getItem("accessToken") && (status === 410 || status === 404)) {
      await regenerateAnonymousIdAfterAbandonment();
    }
    console.error("Error al recuperar carrito del servidor:", error);
    return null;
  }
};

/** Vincula carrito anónimo al usuario ya autenticado (opcional tras login SPA). */
export const linkTemporaryCartToUser = async () => {
  const aid = localStorage.getItem("cart_anonymous_id");
  if (!aid) return null;
  try {
    const response = await apiClient.post("/orders/cart/link/", {
      anonymous_cart_id: aid,
      anonymous_id: aid,
    });
    if (response.data?.cart_linked) {
      clearGuestCartSnapshot();
      localStorage.removeItem("cart_anonymous_id");
    }
    return response.data;
  } catch (e) {
    return null;
  }
};

/** POST granular: alta/actualización de una línea. */
export const upsertServerCartLine = async (payload) => {
  return apiClient.post("/orders/cart/items/", payload);
};

/** Elimina línea por product_id + variants. */
export const removeServerCartLine = async (payload) => {
  return apiClient.post("/orders/cart/items/remove/", payload);
};

export const fetchCustomerInfoFromTempCart = async () => {
  try {
    const anonymousId = localStorage.getItem("cart_anonymous_id");
    if (!anonymousId) return null;

    const response = await apiClient.get(`/orders/public/temp-cart/${anonymousId}/`);
    return response.data?.customer_info || null;
  } catch (error) {
    console.error("Error al recuperar información del cliente desde el carrito temporal:", error);
    return null;
  }
};

export const convertServerCartToLocal = (serverCart) => {
  if (!serverCart?.products) return [];
  if (
    serverCart.shopping_cart_status === "abandoned" ||
    serverCart.code === "cart_abandoned_or_expired"
  ) {
    return [];
  }

  return serverCart.products.map((item) => {
    const blisterId =
      typeof item.blister_id !== "undefined" && item.blister_id !== null
        ? item.blister_id
        : item.product_details?.blister_id ?? "";
    const pid = item.product_details?.id ?? item.product_id;
    // El servidor incluye el precio vigente calculado con PriceService en el
    // producto. `price_at_time` puede pertenecer a una línea antigua (incluso
    // ser 0), por lo que no debe sobrescribir el precio actual del carrito.
    const currentPrice = Number(item.product_details?.price);
    const savedPrice = Number(item.price_at_time);
    const unitPrice = Number.isFinite(currentPrice) && currentPrice > 0
      ? currentPrice
      : (Number.isFinite(savedPrice) ? savedPrice : 0);
    const basePriceRaw =
      item.base_price ??
      item.product_details?.base_price ??
      null;
    const basePrice =
      basePriceRaw != null && !Number.isNaN(Number(basePriceRaw))
        ? Number(basePriceRaw)
        : null;
    const discountPercentRaw =
      item.discount_percent ??
      item.product_details?.total_discount ??
      item.product_details?.discount ??
      0;
    let discountPercent = Number(discountPercentRaw || 0);
    if (!discountPercent && basePrice != null && basePrice > unitPrice && basePrice > 0) {
      discountPercent = Math.round(((basePrice - unitPrice) / basePrice) * 100);
    }
    const originalPrice =
      basePrice != null && basePrice > unitPrice ? basePrice : undefined;

    return {
    id: pid,
    name: item.product_details?.name,
    price: unitPrice,
    original_price: originalPrice,
    discount: discountPercent,
    total_discount: discountPercent,
    image: item.product_details?.image,
    quantity: item.quantity,
    stock:
      item.product_details?.stock ??
      item.product_details?.available_stock ??
      item.product_details?.current_stock ??
      null,
    variants: item.variants || {},
    variants_label: item.variants_label || "",
    currency: normalizeProductCurrency(item.product_details?.currency),
    has_special_promotion: item.product_details?.has_special_promotion || false,
    special_promotion_name: item.product_details?.special_promotion_name || null,
    blister_id: blisterId,
    lineId: `${pid}::${blisterId}::${normalizeVariantsObject(item.variants)}`,
  };
});
};
